import type { JSX } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionsMenu } from './CompanyUserActionsMenu';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { useCompanyUserStatus } from '../hooks/useCompanyUserStatus';
import type { CompanyUser } from '../types/company-admin.types';
import { type ConfirmAction, makeConfirmHandler } from './CompanyUserActions.utils';

interface CompanyUserActionsProps {
  user: CompanyUser;
  onEdit: (user: CompanyUser) => void;
  isCurrentUser?: boolean | undefined;
}

export function CompanyUserActions({
  user,
  onEdit,
  isCurrentUser,
}: CompanyUserActionsProps): JSX.Element | null {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const { deactivate, activate, sendPasswordReset, isDeactivating, isActivating, isSendingReset } =
    useCompanyUserStatus();

  if (isCurrentUser) return null;

  const handleConfirm = makeConfirmHandler({
    confirmAction,
    sendPasswordReset,
    deactivate,
    userId: user.id,
    clearAction: () => setConfirmAction(null),
  });
  const handleActivate = (): void => {
    activate(user.id, {
      onSuccess: () => toast.success('User activated'),
      onError: () => toast.error('Failed to activate user'),
    });
  };

  return (
    <>
      <ActionsMenu
        user={user}
        onEdit={onEdit}
        isDisabled={isDeactivating || isActivating || isSendingReset}
        onResetPassword={() => setConfirmAction('resetPassword')}
        onDeactivate={() => setConfirmAction('deactivate')}
        onActivate={handleActivate}
      />
      <ConfirmActionDialog
        action={confirmAction}
        email={user.email}
        fullName={user.fullName}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
